import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { createBulkCostEntries, getGroups } from '../../../services/supabaseService';
import { useEffect } from 'react';

const BulkCostImportModal = ({ isOpen, onClose, onSuccess }) => {
  const { currentLanguage, t } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadGroups();
    }
  }, [isOpen]);

  const loadGroups = async () => {
    try {
      const groupsData = await getGroups();
      setGroups(groupsData || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const groupOptions = groups?.map(group => ({
    value: group?.id,
    label: `${group?.name} (${group?.type})`
  }));

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      const fileExtension = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
      if (fileExtension === 'csv') {
        setFile(selectedFile);
        parseCSVFile(selectedFile);
      } else {
        alert(t('financial.selectCSVFile'));
      }
    }
  };

  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e?.target?.result;
      const lines = text?.split('\n')?.filter(line => line?.trim());
      
      if (lines?.length < 2) {
        alert(t('financial.emptyCSVFile'));
        return;
      }

      const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
      const data = [];

      for (let i = 1; i < lines?.length; i++) {
        const values = lines?.[i]?.split(',')?.map(v => v?.trim());
        if (values?.length >= 3) {
          const entry = {
            category: values?.[0] || 'other',
            description: values?.[1] || '',
            amount: parseFloat(values?.[2]) || 0,
            quantity: parseInt(values?.[3]) || 1,
            vendor: values?.[4] || '',
            referenceNumber: values?.[5] || '',
            entryDate: values?.[6] || new Date()?.toISOString()?.split('T')?.[0],
            notes: values?.[7] || ''
          };
          data?.push(entry);
        }
      }

      setPreview(data);
      setShowPreview(true);
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!selectedGroup) {
      alert(t('financial.selectGroupFirst'));
      return;
    }

    if (preview?.length === 0) {
      alert(t('financial.noDataToImport'));
      return;
    }

    setLoading(true);

    try {
      const costEntries = preview?.map(entry => ({
        ...entry,
        groupId: selectedGroup
      }));

      await createBulkCostEntries(costEntries);
      
      alert(`${costEntries?.length} ${t('financial.costsImportedSuccess')}`);
      
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error importing costs:', error);
      alert(t('financial.errorImporting'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setSelectedGroup('');
    setPreview([]);
    setShowPreview(false);
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = 'category,description,amount,quantity,vendor,reference_number,entry_date,notes\naccommodation,Hotel Booking,5000,10,Hotel XYZ,REF001,2026-02-10,Group accommodation\ntransport,Bus Rental,3000,2,Transport Co,REF002,2026-02-10,Round trip';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cost_import_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-warm-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {t('financial.bulkCostImport')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
          />
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="bg-muted rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">
              {t('financial.importInstructions')}
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>{currentLanguage === 'fr' ? 'Téléchargez le modèle CSV ci-dessous' : 'قم بتنزيل نموذج CSV أدناه'}</li>
              <li>{currentLanguage === 'fr' ? 'Remplissez les données de coûts' : 'املأ بيانات التكاليف'}</li>
              <li>{currentLanguage === 'fr' ? 'Catégories: accommodation, transport, meals, guides, visas, other' : 'الفئات: accommodation, transport, meals, guides, visas, other'}</li>
              <li>{currentLanguage === 'fr' ? 'Téléchargez le fichier et sélectionnez un groupe' : 'قم بتحميل الملف واختر مجموعة'}</li>
            </ul>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={downloadTemplate}
              className="mt-3"
            >
              {t('financial.downloadTemplate')}
            </Button>
          </div>

          <Select
            label={t('financial.selectGroup')}
            options={groupOptions}
            value={selectedGroup}
            onChange={setSelectedGroup}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('financial.uploadCSV')}
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
            />
          </div>

          {showPreview && preview?.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('financial.preview')} ({preview?.length} {t('financial.entries')})
                </h3>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">{t('financial.category')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">{t('financial.description')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">{t('financial.amount')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">{t('financial.quantity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview?.map((entry, index) => (
                      <tr key={index} className="border-t border-border">
                        <td className="px-4 py-2 text-sm text-foreground">{entry?.category}</td>
                        <td className="px-4 py-2 text-sm text-foreground">{entry?.description}</td>
                        <td className="px-4 py-2 text-sm text-foreground text-right data-text">{entry?.amount?.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-foreground text-right data-text">{entry?.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {t('financial.cancel')}
            </Button>
            <Button
              type="button"
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={handleImport}
              loading={loading}
              disabled={!selectedGroup || preview?.length === 0}
            >
              {loading ? t('financial.importing') : t('financial.importCosts')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkCostImportModal;